// Pure top-down 2D movement against a tile solidity grid. The renderer and
// controller consume these; they carry no three.js or DOM dependencies so the
// collision behaviour is unit-testable and deterministic.
import type { Tilemap2D } from '../../content/schema/map';

/** World units per tile. The tilemap occupies x in [0, width), y in [0, height). */
export const TILE_SIZE = 1;

/** The player's collision half-extent in tiles (a square AABB). */
export const PLAYER_HALF = 0.35;

export interface SolidityGrid {
  width: number;
  height: number;
  solid: boolean[];
}

/** Build the walkability grid: a cell is solid when its tile is solid or
 * empty, or a blocking entity sits on it. */
export function buildSolidityGrid(map: Tilemap2D): SolidityGrid {
  const solidById = new Map(map.tiles.map((t) => [t.id, t.solid === true]));
  const solid = map.cells.map((cell) => (cell === null ? true : (solidById.get(cell) ?? true)));
  for (const entity of map.entities ?? []) {
    if (!entity.blocking) continue;
    const cx = Math.floor(entity.x);
    const cy = Math.floor(entity.y);
    if (cx < 0 || cy < 0 || cx >= map.width || cy >= map.height) continue;
    solid[cy * map.width + cx] = true;
  }
  return { width: map.width, height: map.height, solid };
}

export function isSolidAt(grid: SolidityGrid, x: number, y: number): boolean {
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  if (cx < 0 || cy < 0 || cx >= grid.width || cy >= grid.height) return true;
  return grid.solid[cy * grid.width + cx] ?? true;
}

function boxBlocked(grid: SolidityGrid, x: number, y: number): boolean {
  return (
    isSolidAt(grid, x - PLAYER_HALF, y - PLAYER_HALF) ||
    isSolidAt(grid, x + PLAYER_HALF, y - PLAYER_HALF) ||
    isSolidAt(grid, x - PLAYER_HALF, y + PLAYER_HALF) ||
    isSolidAt(grid, x + PLAYER_HALF, y + PLAYER_HALF)
  );
}

/**
 * Advance the player by velocity * dt with axis-separated AABB collision, so
 * sliding along walls works. Returns the new position.
 */
export function stepTopDown(
  pos: { x: number; y: number },
  vel: { x: number; y: number },
  grid: SolidityGrid,
  dt: number,
): { x: number; y: number } {
  let x = pos.x;
  let y = pos.y;
  const nx = x + vel.x * dt;
  if (!boxBlocked(grid, nx, y)) x = nx;
  const ny = y + vel.y * dt;
  if (!boxBlocked(grid, x, ny)) y = ny;
  return { x, y };
}

/** The map's spawn point, or the first walkable cell centre, or the centre. */
export function findSpawn(map: Tilemap2D, grid: SolidityGrid): { x: number; y: number } {
  if (map.spawn && !boxBlocked(grid, map.spawn.x + 0.5, map.spawn.y + 0.5)) {
    return { x: map.spawn.x + 0.5, y: map.spawn.y + 0.5 };
  }
  for (let i = 0; i < grid.solid.length; i++) {
    if (!grid.solid[i]) {
      const x = (i % grid.width) + 0.5;
      const y = Math.floor(i / grid.width) + 0.5;
      if (!boxBlocked(grid, x, y)) return { x, y };
    }
  }
  return { x: grid.width / 2, y: grid.height / 2 };
}
